<template>
    <div class="card">
        <div class="card-body">
            <div id="graph-div" style="width: 100%; height: 300px; position: relative;">
                <vue-flow v-model="elements" :default-zoom="dynamicZoom" :min-zoom="0.3" :max-zoom="2"
                    :node-dimensions="nodeDimensions" :fit-view-on-init="true" @pane-ready="onReady"
                    @nodeMouseEnter="showTooltip" @nodeMouseLeave="hideTooltip">
                    <template #node-custom="nodeProps">
                        <div class="custom-node" :class="{ 'root-node': nodeProps.data.isRoot }">
                            {{ nodeProps.data.name }}
                        </div>
                    </template>
                    <background pattern-color="#aaa" gap="16" />
                    <controls />
                    <mini-map />
                </vue-flow>
            </div>
        </div>
    </div>
</template>

<script>
import { VueFlow, Background, Controls, MiniMap, useVueFlow } from '@vue-flow/core';
import { computed, onMounted, ref, watch, readonly } from 'vue';
import { DateTimeFormat } from '../utils/commonFunction';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';

export default {
    name: 'VueflowGraph',
    components: {
        VueFlow,
        Background,
        Controls,
        MiniMap
    },
    props: ["data", "name", "hoverData", "kind"],
    setup(props) {
        const { fitView } = useVueFlow();
        const elements = ref([]);
        const nodeDimensions = ref({ width: 200, height: 50 });
        const dynamicZoom = ref(0.8);
        
        // Calculate optimal zoom based on data
        const calculateZoom = computed(() => {
            const leafNodes = props.data.filter(job => job.jobDetails);
            const totalNodes = leafNodes.length;
            
            if (totalNodes <= 3) {
                return 1.0; // Less zoom for fewer nodes
            } else if (totalNodes <= 6) {
                return 0.8; // Medium zoom
            } else {
                return 0.6; // More zoom for many nodes
            }
        });

        const onReady = () => {
            setTimeout(() => {
                fitView({ padding: 0.2 });
            }, 20);
        };

        const calculateNodes = computed(() => {
            const nodeArray = [];
            const edgeArray = [];

            // Check if this is a pipeline type job
            const isPipeline = props.kind === 'pipeline';

            if (!isPipeline) {
                // Pipeline: Sequential graph structure
                let parentId = null;
                const indent = 200;
                const rowHeight = 120;

                props.data.forEach((job, index) => {
                    const jobDetail = job.jobDetails;
                    if (!jobDetail) return;

                    const nodeId = `node-${jobDetail.name}-${index}`;
                    const nodeData = { ...jobDetail, name: jobDetail.name };

                    nodeArray.push({
                        id: nodeId,
                        type: 'custom',
                        position: { x: index * indent, y: index * rowHeight },
                        data: nodeData
                    });

                    if (parentId) {
                        edgeArray.push({
                            id: `edge-${parentId}-${nodeId}`,
                            source: parentId,
                            target: nodeId,
                            type: 'smoothstep',
                            markerEnd: 'arrowclosed',
                            animated: true,
                            style: { stroke: '#6c757d', strokeWidth: 2 }
                        });
                    }

                    parentId = nodeId;
                });
            } else {
                // Non-pipeline: Tree structure
                const rootNodeId = 'root-node';
                const leafNodes = props.data.filter(job => job.jobDetails);
                const totalNodes = leafNodes.length;
                
                // Calculate dynamic spacing based on content
                const maxNameLength = Math.max(...leafNodes.map(job => job.jobDetails.name.length));
                const baseHorizontalSpacing = Math.max(300, maxNameLength * 8); // Minimum 300px, 8px per character
                const horizontalSpacing = Math.min(baseHorizontalSpacing, 450); // Cap at 450px
                const verticalSpacing = 150;
                const startY = 120;
                
                // Calculate nodes per row with better distribution
                let nodesPerRow;
                if (totalNodes <= 2) {
                    nodesPerRow = totalNodes;
                } else if (totalNodes <= 4) {
                    nodesPerRow = 2;
                } else if (totalNodes <= 6) {
                    nodesPerRow = 3;
                } else if (totalNodes <= 9) {
                    nodesPerRow = 3;
                } else {
                    nodesPerRow = 4; // Max 4 nodes per row to avoid overcrowding
                }
                
                // Create root node
                nodeArray.push({
                    id: rootNodeId,
                    type: 'custom',
                    position: { x: 0, y: 0 },
                    data: { name: props.name || 'Job Stack', isRoot: true }
                });

                // Create child nodes
                leafNodes.forEach((job, index) => {
                    const jobDetail = job.jobDetails;
                    const nodeId = `job-${jobDetail.name}-${index}`;
                    
                    const row = Math.floor(index / nodesPerRow);
                    const col = index % nodesPerRow;
                    
                    const nodesInCurrentRow = Math.min(nodesPerRow, totalNodes - row * nodesPerRow);
                    const currentRowWidth = (nodesInCurrentRow - 1) * horizontalSpacing;
                    const currentRowStartX = -currentRowWidth / 2;
                    
                    // Add extra buffer to prevent overlap
                    const x = currentRowStartX + (col * horizontalSpacing);
                    const y = startY + (row * verticalSpacing);
                    
                    // Ensure minimum spacing between nodes
                    const buffer = 20;
                    const adjustedX = x + (col > 0 ? buffer * col : 0);

                    const nodeData = { ...jobDetail, name: jobDetail.name };

                    nodeArray.push({
                        id: nodeId,
                        type: 'custom',
                        position: { x: adjustedX, y },
                        data: nodeData
                    });

                    edgeArray.push({
                        id: `edge-${rootNodeId}-${nodeId}`,
                        source: rootNodeId,
                        target: nodeId,
                        type: 'smoothstep',
                        markerEnd: 'arrowclosed',
                        animated: true,
                        style: { stroke: '#6c757d', strokeWidth: 2 }
                    });
                });
            }

            return [...nodeArray, ...edgeArray];
        });

        // Watch for zoom changes and update zoom ref
        watch(calculateZoom, (newZoom) => {
            dynamicZoom.value = newZoom;
        }, { immediate: true });
        
        onMounted(() => {
            elements.value = calculateNodes.value;
        });

        return {
            elements,
            nodeDimensions,
            onReady,
            dynamicZoom: readonly(dynamicZoom)
        };
    },
    methods: {
        showTooltip(event) {
            // Extract node details and mouse event
            const { id } = event.node;
            const mouseEvent = event.event;

            // Find the node data
            const node = this.elements.find(el => el.id === id && !el.source);

            if (!node) return;

            // Create tooltip element
            const tooltip = document.createElement('div');
            
            // Generate tooltip content
            let tooltipContent = '';
            const fieldsToShow = this.hoverData?.length ? this.hoverData : Object.keys(node.data);
            
            fieldsToShow.forEach(field => {
                const value = node.data[field];
                if (value !== undefined && value !== null && field !== 'isRoot') {
                    const displayValue = field.includes('_at') || field.includes('_time') ? 
                        DateTimeFormat(value) : value;
                    const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    tooltipContent += `<strong>${fieldLabel}: </strong> ${displayValue}<br>`;
                }
            });

            tooltip.innerHTML = `<div>${tooltipContent}</div>`;

            // Position the tooltip near the mouse
            tooltip.style.left = mouseEvent.pageX + 5 + 'px';
            tooltip.style.top = mouseEvent.pageY + 5 + 'px';

            // Remove any existing tooltips
            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) {
                existingTooltip.remove();
            }

            // Add tooltip class for easy removal
            tooltip.classList.add('vueflow-tooltip');

            // Add to document
            document.body.appendChild(tooltip);
        },

        hideTooltip() {
            // Remove tooltip when mouse leaves node
            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) {
                existingTooltip.remove();
            }
        }
    }
};
</script>

<style>
.vue-flow {
    width: 100%;
    height: 100%;
}

.vue-flow__node {
    cursor: pointer;
}

.custom-node {
    padding: 12px 16px;
    border-radius: 6px;
    border: 2px solid #dee2e6;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 160px;
    max-width: 280px;
    word-wrap: break-word;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    font-size: 14px;
    font-weight: 500;
    color: #495057;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}


.vue-flow__edge path {
    stroke: #6c757d;
    opacity: 0.8;
    stroke-width: 2px;
}



.vueflow-tooltip {
    position: absolute;
    pointer-events: none;
    padding: 8px 12px;
    border-radius: 6px;
    background-color: #6E84A3;
    color: white;
    font-size: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-width: 250px;
}
</style>